<template>
    <service-card
        :service-tips="serviceTips"
        :service="service"
        icon="mdi-car"
        :is-first="isFirst"
        :order="order"
        :variants-available="variantsAvailable"
        @show-info="showInfo"
        @modify-service="$emit('modify-service', $event)"
        @minimized-change="minimizedChange"
    >
        <template v-slot:checkbox>
            <slot name="checkbox" />
        </template>
        <template v-slot:icons>
            <slot name="icons" />
        </template>
        <template v-slot:orderInfo>
            <order-info-base
                :service="service"
                :service-check-in="serviceCheckIn"
                :service-check-out="serviceCheckOut"
                :service-image="mainImage"
                :is-package="isPackage"
                :tariff-name="service.serviceDetails[0].tariffName"
                :order-end-date="orderEndDate"
                :order="order"
                @cancel-service="$emit('cancel-service', $event)"
                @modify-service="$emit('modify-service', $event)"
            >
                <v-skeleton-loader v-if="isInfoLoading" type="paragraph" />
                <div v-if="pickUpPoint" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start size="x-small">{{ pickUpIcon }}</v-icon>
                        <span class="text-body-2">{{ $t('transfers.from') }}</span>
                    </span>
                    <div class="order-info-base-info__text d-flex align-center flex-wrap" style="gap: 0 6px">
                        {{ `${pickUpPoint.pointName}${pickUpPointInfo.address && `, ${pickUpPointInfo.address}`}` }}
                        <template v-if="pickUpPointInfo.address">
                            <span
                                v-if="pickUpPointInfo.latitude && pickUpPointInfo.longitude"
                                class="text-body-2 text-link text-decoration-underline text-lowercase"
                                @click="
                                    $emit('show-on-map', {
                                        latitude: pickUpPointInfo.latitude,
                                        longitude: pickUpPointInfo.longitude,
                                    })
                                "
                            >
                                {{ $t('show_on_map') }}
                            </span>
                        </template>
                    </div>
                </div>
                <div v-if="dropOffPoint" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start size="x-small">{{ dropOffIcon }}</v-icon>
                        <span class="text-body-2">{{ $t('transfers.to') }}</span>
                    </span>
                    <div class="order-info-base-info__text d-flex align-center flex-wrap" style="gap: 0 6px">
                        {{ `${dropOffPoint.pointName}${pickUpPointInfo.address && `, ${pickUpPointInfo.address}`}` }}
                        <template v-if="pickUpPointInfo.address">
                            <span
                                v-if="dropOffPointInfo.latitude && dropOffPointInfo.longitude"
                                class="text-body-2 text-link text-decoration-underline text-lowercase"
                                @click="
                                    $emit('show-on-map', {
                                        latitude: dropOffPointInfo.latitude,
                                        longitude: dropOffPointInfo.longitude,
                                    })
                                "
                            >
                                {{ $t('show_on_map') }}
                            </span>
                        </template>
                    </div>
                </div>
                <div v-if="service.status === 'Quote' && !isReadOnly" class="d-flex align-center">
                    <v-icon start size="small" color="primary" @click="fillCarBookingDataModal = true">
                        mdi-pencil
                    </v-icon>
                    <span class="text-caption cursor-pointer text-primary" @click="fillCarBookingDataModal = true">
                        {{ $t('enter_details') }}
                    </span>
                    <fill-car-booking-data-modal
                        v-if="isMinimized === false || infoLoadingFinished"
                        v-model="fillCarBookingDataModal"
                        :service="service"
                        :order="order"
                    />
                </div>
            </order-info-base>
        </template>
        <template v-slot:serviceInfo>
            <v-dialog v-model="modal" width="600" :scrim="false">
                <v-card class="pa-1 pa-md-3">
                    <div class="modal-close-btn-block">
                        <v-icon class="v-card-close" @click="modal = false">mdi-close</v-icon>
                    </div>
                    <div class="text-h6">
                        {{ info.modelName }}
                    </div>
                    <span v-if="service.productNameForSupplier && isTO1" class="text-caption text--secondary">
                        {{ service.productNameForSupplier }}
                    </span>
                    <div class="text-subtitle-2">
                        {{ info.carRentCompanyName }}
                    </div>
                    <div
                        v-if="isTO1 && $conf.public.account.enableCustomProductInfo && !ownProduct"
                        class="full-width d-flex align-center justify-center"
                    >
                        <template v-if="!customProductInfoMode">
                            <v-spacer />
                            <v-btn variant="text" color="primary" @click="customProductInfoMode = true">
                                {{ $t('edit_custom_product_info') }}
                            </v-btn>
                        </template>
                    </div>
                    <custom-product-info-form
                        v-if="customProductInfoMode"
                        :service="service"
                        @close="customProductInfoMode = false"
                        @close-modal="closeModal"
                    />
                    <template v-else>
                        <v-tabs v-model="tab" hide-slider>
                            <v-tab class="text-uppercase">
                                {{ $t('description') }}
                            </v-tab>
                            <v-tab class="text-uppercase">
                                {{ `${$t('map')}` }}
                            </v-tab>
                        </v-tabs>
                        <v-window v-model="tab">
                            <v-window-item>
                                <v-row>
                                    <v-col cols="12" md="4">
                                        <v-carousel
                                            height="170px"
                                            hide-delimiters
                                            :show-arrows="info.images && info.images.length > 1 ? 'hover' : false"
                                        >
                                            <v-carousel-item
                                                v-for="(image, imageIndex) in info.images"
                                                :key="imageIndex"
                                            >
                                                <v-img :src="$filters.imageUrl(image.url, 300)" height="170px" cover />
                                            </v-carousel-item>
                                        </v-carousel>
                                    </v-col>
                                    <v-col cols="12" md="8">
                                        <div v-if="info.shortDescription" class="mt-2">
                                            <span class="d-block text-subtitle-1 font-weight-medium">
                                                {{ $t('description_type.short') }}
                                            </span>
                                            <span v-html="info.shortDescription" />
                                        </div>
                                        <div v-if="info.description" class="mt-2">
                                            <span class="d-block text-subtitle-1 font-weight-medium">
                                                {{ $t('description_type.general') }}
                                            </span>
                                            <span v-html="info.description" />
                                        </div>
                                        <div v-if="info.pleaseNote" class="mt-2">
                                            <span class="d-block text-subtitle-1 font-weight-medium">
                                                {{ $t('important_information') }}
                                            </span>
                                            <span v-html="info.pleaseNote" />
                                        </div>
                                        <template v-if="info.features && info.features.length">
                                            <span class="d-block text-subtitle-1 font-weight-medium">
                                                {{ `${$t('included')}:` }}
                                            </span>
                                            <ul>
                                                <template v-for="feature in info.features" :key="feature">
                                                    <li
                                                        class="text-body-2"
                                                        v-text="$t(`carsRent.${feature.toLowerCase()}`)"
                                                    />
                                                </template>
                                            </ul>
                                        </template>
                                    </v-col>
                                </v-row>
                            </v-window-item>
                            <v-window-item>
                                <cars-map
                                    v-model="mapExpanded"
                                    :offers="mapPoints"
                                    :parent="true"
                                    style="max-height: 500px; position: unset"
                                />
                            </v-window-item>
                        </v-window>
                    </template>
                    <v-skeleton-loader v-if="isInfoLoading" type="paragraph" />
                </v-card>
            </v-dialog>
        </template>
    </service-card>
</template>

<script lang="ts">
    import {Component, Prop, Inject} from 'vue-facing-decorator'
    import ServiceCard from '~src/components/account/orderServices/serviceCard.src'
    import CarsMap from '@/components/carsRent/search/CarsMap.vue'
    import {ServiceBase} from '@/components/account/orderServices/ServiceBase.vue'
    import FillCarBookingDataModal from '@/components/account/modals/newOrderRequest/FillCarBookingDataModal.vue'

    import CustomProductInfoForm from '@/components/account/orderServices/CustomProductInfoForm.vue'
    import OrderInfoBase from '~src/components/account/orderServices/orderInfoBase.src'

    @Component({
        components: {
            CustomProductInfoForm,
            FillCarBookingDataModal,
            ServiceCard,
            OrderInfoBase,
            CarsMap,
        },
    })
    export class CarService extends ServiceBase {
        @Prop({required: true}) service
        @Prop({default: false}) isPackage
        @Prop() isFirst
        @Inject({default: false}) isReadOnly

        modal = false
        carInfo = null
        mapExpanded = false
        mapPoints = []
        fillCarBookingDataModal = false
        pickUpPointInfo = {}
        dropOffPointInfo = {}
        customProductInfoMode = false
        tab = null

        closeModal() {
            this.modal = false
        }

        async loadProductInfo() {
            this.isInfoLoading = true

            await this.getAdditionalInfo()

            this.isInfoLoading = false
            this.infoLoadingFinished = true
        }

        async getAdditionalInfo() {
            this.carInfo = await this.getCarInfo()
            const promises = []
            promises.push(this.getPickUpPointInfo('pickup'))
            promises.push(this.getPickUpPointInfo('return'))
            const [pickUpPointInfo, dropOffPointInfo] = await Promise.all(promises)

            pickUpPointInfo.pointType = 'pickup'
            dropOffPointInfo.pointType = 'return'
            this.pickUpPointInfo = pickUpPointInfo
            this.dropOffPointInfo = dropOffPointInfo

            this.mapPoints = [pickUpPointInfo, dropOffPointInfo]
        }

        async getCarInfo() {
            const processId = this.service.processId.replace(/^.{6}/, '')
            return await this.$api.carRentInfo.get({processId})
        }

        async getPickUpPointInfo(type) {
            try {
                const processId = this.service.processId
                const res = await this.$api.pointInfo.get({processId, pointType: type})
                return res || {}
            } catch (e) {
                return {}
            }
        }

        showInfo() {
            this.customProductInfoMode = false

            if (!this.infoLoadingFinished) {
                this.loadProductInfo()
            }

            this.modal = true
        }

        getIcon(place) {
            switch (this.getPointType(place)) {
                case 'airport':
                    return 'mdi-airport'
                case 'hotel':
                    return 'mdi-hotel'
                default:
                    return 'mdi-city'
            }
        }

        getPointType(place) {
            if (this.serviceDetails[place]?.airportCode) {
                return 'airport'
            } else if (this.serviceDetails[place]?.hotelCode) {
                return 'hotel'
            }
            return 'address'
        }

        get serviceDetails() {
            return this.service.serviceDetails[0]
        }

        get serviceCheckIn() {
            const pickUpTime = this.$filters.hoursFromDate(this.service.startDateTime)
            const date = this.$filters.dateFormat(this.service.startDateTime)
            return `<span>${date}</span> <small>${pickUpTime}</small>`
        }

        get serviceCheckOut() {
            const dropOffTime = this.$filters.hoursFromDate(this.service.endDateTime)
            const date = this.$filters.dateFormat(this.service.endDateTime)
            return `<span>${date}</span> <small>${dropOffTime}</small>`
        }

        get pickUpPoint() {
            return this.serviceDetails.pickUpPoint
        }

        get dropOffPoint() {
            return this.serviceDetails.dropOffPoint
        }

        get pickUpIcon() {
            return this.getIcon('pickUpPoint')
        }

        get dropOffIcon() {
            return this.getIcon('dropOffPoint')
        }

        get mainImage() {
            if (this.carInfo && this.carInfo.images && this.carInfo.images.length) {
                let image = this.carInfo.images.find(el => el.mainImage)
                if (!image) {
                    image = this.carInfo.images[0]
                }
                return image.url
            }
            return null
        }

        get info() {
            return {...this.carInfo, ...{mainImage: this.mainImage}}
        }

        get isTO1() {
            return authStore.isTO1
        }

        get ownProduct() {
            return runtimeStore.ownProduct(this.service.supplierCode)
        }
    }

    export default CarService
</script>
